import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useEnquiryList,
  useEnquiryPriorities,
  useEnquirySources,
  useEnquiryStatuses,
  type EnquiryListParams,
} from "../hooks/useEnquiries";
import { NewEnquiryDrawer } from "../components/NewEnquiryDrawer";
import {
  EnquiryPriorityBadge,
  EnquiryStatusBadge,
  SourceBadge,
} from "../components/EnquiryStatusBadge";

type TabKey = "all" | "new" | "mine" | "closed" | "spam";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "mine", label: "Assigned to me" },
  { key: "closed", label: "Closed" },
  { key: "spam", label: "Spam" },
];

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}

export default function EnquiriesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = !!user?.permissions?.enquiries?.canCreate;

  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [assignee, setAssignee] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const users = useUserList({ limit: 200 });
  const { data: statuses = [] } = useEnquiryStatuses();
  const { data: priorities = [] } = useEnquiryPriorities();
  const { data: sources = [] } = useEnquirySources();

  const params = useMemo<EnquiryListParams>(
    () => ({
      page,
      limit,
      tab,
      search: search.trim() || undefined,
      status: status || undefined,
      priority: priority || undefined,
      assignedToId: assignee || undefined,
      source: sourceFilter ? [sourceFilter] : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [page, limit, tab, search, status, priority, assignee, sourceFilter, dateFrom, dateTo],
  );

  const { data, isLoading, isError, error } = useEnquiryList(params);
  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const summary = data?.summary;

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "CRM" },
        { label: "Enquiries" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Inbox className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Enquiries</h1>
            {summary && (
              <span className="text-sm text-muted-foreground">
                {summary.new} new · {summary.spam} spam
              </span>
            )}
          </div>
          {canCreate && (
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Enquiry
            </Button>
          )}
        </div>

        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setPage(1);
                }}
                className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  tab === t.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-[260px]">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search name, email, subject…"
              value={search}
              onChange={(e) => resetPage(setSearch)(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => resetPage(setStatus)(e.target.value)}
            className="w-40"
          >
            <option value="">All Status</option>
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Select
            value={priority}
            onChange={(e) => resetPage(setPriority)(e.target.value)}
            className="w-36"
          >
            <option value="">All Priority</option>
            {priorities.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
          <Select
            value={assignee}
            onChange={(e) => resetPage(setAssignee)(e.target.value)}
            className="w-44"
          >
            <option value="">All Assignees</option>
            {users.data?.data.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
          <Select
            value={sourceFilter}
            onChange={(e) => resetPage(setSourceFilter)(e.target.value)}
            className="w-40"
          >
            <option value="">All Sources</option>
            {sources.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => resetPage(setDateFrom)(e.target.value)}
            className="w-40"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => resetPage(setDateTo)(e.target.value)}
            className="w-40"
          />
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            Failed to load enquiries: {(error as Error)?.message}
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-xl border bg-card"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading enquiries…
              </div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                No enquiries match the current filters.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Code</th>
                    <th className="px-6 py-3">Source</th>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Subject</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Priority</th>
                    <th className="px-6 py-3">Assigned</th>
                    <th className="px-6 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((e) => (
                    <tr
                      key={e.id}
                      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                      onClick={() => navigate(`/enquiries/${e.id}`)}
                    >
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                        {e.code}
                      </td>
                      <td className="px-6 py-4">
                        <SourceBadge source={e.source} />
                      </td>
                      <td className="px-6 py-4 text-foreground">
                        {e.name ?? "—"}
                        {e.email && (
                          <div className="text-xs text-muted-foreground">
                            {e.email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {e.subject ?? "—"}
                      </td>
                      <td className="px-6 py-4">
                        <EnquiryStatusBadge status={e.status} />
                      </td>
                      <td className="px-6 py-4">
                        <EnquiryPriorityBadge priority={e.priority} />
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {e.assignedToName ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {relativeTime(e.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div
              className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground"
              style={{
                borderTop: "1px solid hsl(210,30%,85%)",
                background: "hsl(210,30%,97%)",
              }}
            >
              <div className="flex items-center gap-2">
                <span>
                  {total === 0 ? 0 : (page - 1) * limit + 1}–
                  {Math.min(page * limit, total)} of {total}
                </span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-[26px] rounded border bg-white px-1"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded border px-2 py-1 disabled:opacity-40"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  ‹
                </button>
                <span>
                  {Math.ceil(total / limit) === 0 ? 0 : page} /{" "}
                  {Math.ceil(total / limit) || 1}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(total / limit)}
                  className="rounded border px-2 py-1 disabled:opacity-40"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}

        <NewEnquiryDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onCreated={(enq) => navigate(`/enquiries/${enq.id}`)}
        />
      </div>
    </AppLayout>
  );
}
